import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useProfileField } from "@/hooks/useHealthProfile";
import { ICondition, MedicalDetailType } from "@/types/health-profile";
import { getMemberInfo } from "@/hooks/useMembers";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { ListItem } from "@/components/ui/atoms/MListItem";
import "@/components/ui/atoms/footer-drawer-done-button.css";
import { IconEdit, IconCircleX } from "@tabler/icons-react";
import MLoader from "@/components/ui/atoms/MLoader";
import { MDialog } from "@/components/ui/atoms/MDialog";
import EmptyAfterDeleteDialog from "@/components/ui/molecules/EmptyAfterDeleteDialog";
import FormHeader from "@/components/ui/organisms/MHeader";
import { useTranslation } from "react-i18next";

import { useNavigationSource } from "@/hooks/useNavigationSource";

const Conditions: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Navigation source
  const { navigateBackIfExists } = useNavigationSource();
  const memberInfo = getMemberInfo();
  const memberId = memberInfo.id;
  // No auto-redirect useEffect

  const { showToast } = useToast();
  const {
    fieldValue: conditions = [],
    saveField: saveConditions,
    loading,
  } = useProfileField<ICondition[]>(memberId, MedicalDetailType.CONDITIONS, []);

  const handleDelete = async () => {
    if (!conditionToDelete) return;
    try {
      const updatedConditions = conditions.filter(
        (c) => c !== conditionToDelete
      );
      await saveConditions(updatedConditions);
      setShowDeleteModal(false);
      setConditionToDelete(null);
      if (updatedConditions.length === 0) {
        setShowEmptyDialog(true);
      }
    } catch (error) {
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
      setShowDeleteModal(false);
      setConditionToDelete(null);
    }
  };

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEmptyDialog, setShowEmptyDialog] = useState(false);
  const [conditionToDelete, setConditionToDelete] = useState<ICondition | null>(
    null
  );

  const getSecondaryText = (condition: ICondition) => {
    if (condition.diagnosisYear && condition.diagnosisMonth) {
      const date = new Date(
        condition.diagnosisYear,
        condition.diagnosisMonth - 1
      );
      const monthName = date.toLocaleString("default", { month: "long" });
      return t("conditions.since", {
        year: `${monthName} ${condition.diagnosisYear}`,
      });
    } else if (condition.diagnosisDate) {
      const year = new Date(condition.diagnosisDate).getFullYear();
      return t("conditions.since", { year: year });
    }
    return t("conditions.dateOfDiagnosisNotMentioned");
  };

  if (loading) return <MLoader />;

  return (
    <div className="profile-page-root">
      <FormHeader
        module="conditions"
        memberInfo={memberInfo}
        onAdd={() =>
          navigate("/profile/health-profile/conditions/form?mode=add")
        }
        showGradient
        onBack={() => {
          const didNavigate = navigateBackIfExists();
          if (!didNavigate) {
            navigate("/profile/health-profile");
          }
        }}
      />
      <div className="profile-flex-col-gap">
        {conditions.map((condition, idx) => (
          <ListItem
            key={`${condition.name}-${
              condition.diagnosisYear || condition.diagnosisDate
            }-${condition.diagnosisMonth || ""}-${idx}`}
            title={condition.name}
            secondaryText={getSecondaryText(condition)}
            usage="crud"
            variant="purple"
            type="2-line"
            actions={[
              {
                id: "edit",
                label: t("common.edit"),
                icon: <IconEdit />,
                onClick: () => {
                  navigate(
                    `/profile/health-profile/conditions/form?mode=edit&index=${idx}&memberId=${memberId}`
                  );
                },
              },
              {
                id: "delete",
                label: t("common.delete"),
                icon: <IconCircleX />,
                onClick: () => {
                  if (showDeleteModal) return; // Prevent multiple modals
                  setConditionToDelete(condition);
                  setShowDeleteModal(true);
                },
                className: "delete",
              },
            ]}
          />
        ))}
      </div>
      <MDialog
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setConditionToDelete(null);
        }}
        title={t("conditions.deleteTitle")}
        description={t(
          `conditions.deleteDescription${
            memberInfo?.onBehalf ? "_on_behalf" : ""
          }`
        )}
        primaryCtaText={t("common.yes")}
        onPrimaryCta={handleDelete}
        secondaryCtaText={t("common.no")}
        onSecondaryCta={() => {
          setShowDeleteModal(false);
          setConditionToDelete(null);
        }}
      />
      <EmptyAfterDeleteDialog
        isOpen={showEmptyDialog}
        onConfirm={() => {
          setShowEmptyDialog(false);
          const didNavigate = navigateBackIfExists();
          if (!didNavigate) navigate("/profile/health-profile");
        }}
      />
    </div>
  );
};

export default Conditions;
